import { AnalyticsContext } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useContext } from 'react';
import type { AutoPopulateStayZonesPayload, Maybe } from '@graphql/generated';
import { useAutoPopulateStayZonesMutation, DeferredOperationJobStatus } from '@graphql/generated';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';
import { useLocaleContext } from '@localization/context/LocaleContext';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import type { StayZonesContext } from '../stayZonesAutoPopulation.types';

const OPERATION_NAME = 'autoPopulationStayZones';

interface CallAutoPopulationPayload
  extends Readonly<{
    eventId?: Maybe<string>;
    location?: Maybe<LatLngLiteral>;
    context: StayZonesContext;
    scenario?: string;
  }> {}

type AutoPopulationResponse = Readonly<{
  idempotentId?: Maybe<string>;
  status: DeferredOperationJobStatus;
  error?: Maybe<string>;
}>;

/**
 * Custom hook to call the auto-populate stay zones mutation.
 * @returns A tuple containing the callAutoPopulation function and the loading state.
 */
export const useStayZonesAutoPopulationApi = () => {
  const analytics = useContext(AnalyticsContext);
  const { language: locale } = useLocaleContext();

  const [autoPopulateStayZones, { loading }] = useAutoPopulateStayZonesMutation();

  const callAutoPopulation = useEventCallback(async (payloadArgs: CallAutoPopulationPayload): Promise<AutoPopulationResponse> => {
    const { eventId, location, context, scenario } = payloadArgs;

    if (!eventId) {
      const error = new Error('Missing event id');
      addError(error, {
        context,
        latitude: location?.lat,
        longitude: location?.lng,
        isAsync: true,
        scenario,
        operation: OPERATION_NAME,
      });
      return { status: DeferredOperationJobStatus.FAILED, error: error.message };
    }

    const payload: AutoPopulateStayZonesPayload = {
      autoPopulate: true,
      eventId,
      locale,
      location,
      scenario,
    };

    try {
      const response = await autoPopulateStayZones({ variables: { payload } });

      if (!response.data || !response.data.autoPopulateStayZones) {
        const error = 'No response data';
        addError(new Error(error), {
          context,
          eventId: payload.eventId,
          latitude: payload.location?.lat,
          longitude: payload.location?.lng,
          isAsync: true,
          scenario,
          operation: OPERATION_NAME,
        });
        return { status: DeferredOperationJobStatus.FAILED, error };
      }

      const { status, error } = response.data.autoPopulateStayZones;

      analytics.track({
        category: 'accommodations',
        action: `TravelButtonInteracted`,
        label: 'autoPopulateStayZones',
        extraInfo: {
          name: `autoPopulateStayZones`,
          eventId: payload.eventId,
          latitude: payload.location?.lat,
          longitude: payload.location?.lng,
          isAsync: true,
          scenario,
          source: context,
          status,
        },
      });

      return { status, error };
    } catch (error) {
      const { errorExplanation, statusCode, isTimeout } = parseNetworkErrorDetails(error);
      addError(errorExplanation, {
        context,
        eventId: payload.eventId,
        latitude: payload.location?.lat,
        longitude: payload.location?.lng,
        isAsync: true,
        scenario,
        statusCode,
        isTimeout,
        originalError: error,
        operation: OPERATION_NAME,
      });
      throw error;
    }
  });

  return { callAutoPopulation, loading };
};
